import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Building2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const dismissKey = (orgId) => `mlcn_org_consent_dismissed_${orgId}`;

/** PATH A consent step: an org invited this caregiver (org code at signup or
 * redeem). One question, two buttons. Declining keeps her fully functional —
 * Settings shows "[Org] is waiting to connect" with a one-tap accept. */
export default function OrgConsentPrompt() {
    const [pending, setPending] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/care-access/pending");
            const next = (r.data?.pending || []).find((p) => {
                try { return !localStorage.getItem(dismissKey(p.org_id)); } catch { return true; }
            });
            setPending(next || null);
        } catch { setPending(null); }
    };

    useEffect(() => {
        load();
        window.addEventListener("ml-org-invited", load);
        return () => window.removeEventListener("ml-org-invited", load);
    }, []);

    if (!pending) return null;

    const accept = async () => {
        setBusy(true);
        try {
            await api.post(`/care-access/${pending.org_id}/consent`, { accept: true });
            toast.success(`You're sharing with ${pending.org_name}. Manage this anytime in Settings.`);
            setPending(null);
        } catch {
            toast.error("Something went wrong — you can also accept from Settings.");
        } finally { setBusy(false); }
    };

    const later = async () => {
        try { localStorage.setItem(dismissKey(pending.org_id), "1"); } catch { /* ignore */ }
        api.post(`/care-access/${pending.org_id}/consent`, { accept: false }).catch(() => {});
        setPending(null);
    };

    return createPortal(
        <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/30 px-4 pb-6 sm:pb-4"
            data-testid="org-consent-prompt">
            <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-teal-soft text-teal-dark">
                    <Building2 size={18} strokeWidth={1.8} />
                </span>
                <p className="mt-3 font-heading text-lg font-semibold text-[#1F2624] leading-snug" data-testid="org-consent-title">
                    {pending.org_name} invited you to Memory Lane. Would you like your care team to see what you share here?
                </p>
                <p className="mt-2 text-sm text-[#4C5551] leading-relaxed">
                    They&apos;d see your check-ins, behavior logs, and summaries — read-only. Your conversations
                    with Lane stay private. You can turn this off anytime in Settings.
                </p>
                <div className="mt-5 space-y-2">
                    <button type="button" onClick={accept} disabled={busy} data-testid="org-consent-accept"
                        className="w-full bg-teal-dark hover:bg-teal-dark/90 text-white rounded-2xl px-5 py-3 text-sm font-semibold min-h-[48px] disabled:opacity-50">
                        Yes, share with my care team
                    </button>
                    <button type="button" onClick={later} disabled={busy} data-testid="org-consent-later"
                        className="w-full bg-white border border-cream-dark text-[#4C5551] rounded-2xl px-5 py-3 text-sm font-medium min-h-[48px] hover:border-teal">
                        Not right now
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
